import Breadcrumbs from "@/components/Breadcrumbs";
import ElementPageEditor from "@/components/ElementPage/Editor";
import { ElementDatabaseService } from "@/services/admin-services";
import { DatabaseElement, ElementPageConfiguration } from "@/services/admin-services/models";
import { LocaleService } from "@/services/global-services";
import { Language, Locale } from "@/services/global-services/models";
import { ArrowLeftIcon, CogIcon } from "@heroicons/react/24/outline";
import {
    Box,
    Button,
    Collapse,
    Divider,
    FormControl,
    Grid,
    MenuItem,
    Select,
    Typography,
    useTheme,
} from "@mui/material";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ElementPageConfigurationEditor from "./components/ElementPageConfigurationEditor";
import InlineEditor from "./components/InlineEditor";
import { InlineEditorProvider } from "./components/InlineEditor/context/InlineEditorProvider";
import KeywordEditor from "./components/KeywordEditor";

const breadcrumbs = [
    {
        to: "../..",
        title: "Administration",
    },
    {
        title: "Content",
        to: "..",
    },
    {
        title: "Elements",
        to: "..",
    },
];

const Editor = () => {
    const { identifier, language } = useParams();

    const [breadcrumbTrail, setBreadcrumbTrail] = useState(breadcrumbs);

    const [configurationVisible, setConfigurationVisible] = useState<boolean>(true);
    const [mainLanguage, setMainLanguage] = useState<string>(language!);
    const [secondaryLanguage, setSecondaryLanguage] = useState<string>("");
    const [languages, setLanguages] = useState<Language[]>([]);

    const [loading, setLoading] = useState(true);

    const [pageConfiguration, setPageConfiguration] = useState<ElementPageConfiguration | null>(null);
    const [elements, setElements] = useState<DatabaseElement[]>([]);
    const [element, setElement] = useState<DatabaseElement>();
    const elementService = useMemo(() => new ElementDatabaseService("int", "int-en"), []);

    const theme = useTheme();
    const navigate = useNavigate();

    useEffect(() => {
        Promise.all([
            elementService.get().then((elements) => {
                const currentElement = elements.find((x) => x.id === identifier);
                setElements(elements);
                setElement(currentElement);
            }),
            elementService.getElementPageConfiguration(identifier!).then(setPageConfiguration),
            LocaleService.get().then(parseLanguages),
        ]).then(() => setLoading(false));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (element) {
            setBreadcrumbTrail([
                ...breadcrumbs,
                {
                    to: "",
                    title: element.name,
                },
            ]);
        }
    }, [element]);

    useEffect(() => {
        if (mainLanguage !== language) navigate(`../${identifier}/${mainLanguage}`, { replace: true });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mainLanguage]);

    const parseLanguages = (regions: Locale[]) => {
        const languages: Language[] = [];
        for (let i = 0; i < regions.length; i++) {
            const region = regions[i];

            for (let j = 0; j < region.languages.length; j++) {
                const language = region.languages[j];
                if (languages.findIndex((x) => x.label === language.label) > -1) continue;

                const split = language.slug.split("-");
                languages.push({ ...language, slug: `int-${split[1]}`, regionLabel: "International" });
            }
        }

        setLanguages(languages.sort((a, b) => a.label.localeCompare(b.label)));
    };

    const renderNames = () => {
        return languages.map((language, index) => {
            const isLast = index + 1 === languages.length;
            const locale = element!.objectNames.find((x) => x.item1 === language.slug)?.item2 ?? <em>Missing</em>;

            return (
                <Fragment key={index}>
                    <strong>{language.label}:</strong> {locale}
                    {!isLast && " - "}
                </Fragment>
            );
        });
    };

    if (loading || !element)
        return (
            <Box>
                <Breadcrumbs trail={breadcrumbTrail} />

                <Grid container spacing={4} p={{ xs: 2, md: 4 }}>
                    <Grid size={{ xs: 12, md: 12 }}>
                        <Box sx={{ display: "flex", height: 400, alignItems: "center", flexDirection: "column" }}>
                            <Box sx={{ margin: "auto" }}>
                                <Typography>Loading data...</Typography>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        );

    return (
        <InlineEditorProvider>
            <InlineEditor />

            <Box>
                <Breadcrumbs trail={breadcrumbTrail} />

                <Box m={4} sx={{ display: "flex" }}>
                    <Box sx={{ display: "flex", flex: 1, flexDirection: "column" }}>
                        <Typography variant={"h4"}>{element.name}</Typography>
                        <Typography variant={"subtitle2"}>{renderNames()}</Typography>
                    </Box>
                    <Box sx={{ alignSelf: "center" }}>
                        <Button
                            variant={"contained"}
                            sx={{ ml: "auto" }}
                            startIcon={<CogIcon className="h-5 w-5" />}
                            onClick={() => setConfigurationVisible((x) => !x)}
                        >
                            {configurationVisible ? "Hide" : "Show"} Configuration
                        </Button>
                    </Box>
                </Box>

                <Divider sx={{ marginY: 4 }} />

                <Collapse in={configurationVisible}>
                    <ElementPageConfigurationEditor
                        id={identifier!}
                        configuration={pageConfiguration}
                        onChange={(cfg) => setPageConfiguration(cfg)}
                    />
                    <Divider sx={{ marginY: 4 }} />
                    <KeywordEditor id={identifier!} languages={languages} />
                    <Divider sx={{ marginY: 4 }} />
                </Collapse>

                {!pageConfiguration || !pageConfiguration.templateId ? (
                    <Box sx={{ display: "flex", height: 300, alignItems: "center", flexDirection: "column", mb: 4 }}>
                        <Box sx={{ margin: "auto" }}>
                            <Typography>
                                Please activate a template and save the page information before you continue.
                            </Typography>
                        </Box>
                    </Box>
                ) : (
                    <>
                        <Grid container spacing={2} p={{ xs: theme.spacing(0, 2), md: theme.spacing(0, 4) }}>
                            <Grid size={{ xs: 12, md: 6 }} sx={{ display: "flex", alignItems: "center" }}>
                                <FormControl sx={{ minWidth: 100 }}>
                                    <Select
                                        size="small"
                                        labelId="page-editor-language-select-main"
                                        id="page-editor-language-select-main"
                                        value={mainLanguage}
                                        onChange={(e) => setMainLanguage(e.target.value)}
                                    >
                                        {languages.map((language, index) => (
                                            <MenuItem
                                                key={index}
                                                value={language.slug}
                                                disabled={secondaryLanguage === language.slug}
                                            >
                                                {language.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <ArrowLeftIcon className="h-5 w-5 ml-2 mr-1" />
                                <FormControl sx={{ minWidth: 100 }}>
                                    <Select
                                        size="small"
                                        labelId="page-editor-language-select-secondary"
                                        id="page-editor-language-select-secondary"
                                        value={secondaryLanguage}
                                        onChange={(e) => setSecondaryLanguage(e.target.value)}
                                        displayEmpty
                                    >
                                        <MenuItem value={""}>-</MenuItem>
                                        {languages.map((language, index) => (
                                            <MenuItem
                                                key={index}
                                                value={language.slug}
                                                disabled={mainLanguage === language.slug}
                                            >
                                                {language.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>

                        <Divider sx={{ marginY: 4 }} />

                        <Grid container spacing={2} p={{ xs: 2, md: 4 }}>
                            <Grid size={{ xs: 12, lg: secondaryLanguage ? 6 : 12 }}>
                                <ElementPageEditor
                                    templateId={pageConfiguration.templateId}
                                    language={mainLanguage}
                                    elements={elements}
                                />
                            </Grid>

                            {Boolean(secondaryLanguage) && (
                                <Grid size={{ xs: 12, lg: 6 }}>
                                    <ElementPageEditor
                                        templateId={pageConfiguration.templateId}
                                        language={secondaryLanguage}
                                        contentOverride={mainLanguage}
                                        elements={elements}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </>
                )}
            </Box>
        </InlineEditorProvider>
    );
};

export default Editor;
